import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { Heart, Shield, Globe, Palette, DollarSign, AlertTriangle, TrendingUp } from "lucide-react";
import { API, CHART } from "../../lib/constants";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const ICONS = {
  core_hypothesis: Heart,
  extraction_proof: AlertTriangle,
  system_aware: Shield,
  africa_care_first: Globe,
  creative_orientation: Palette,
  monetization_gender: DollarSign,
};

const DiscoveryCard = ({ discovery }) => {
  const Icon = ICONS[discovery.id] || TrendingUp;
  return (
    <div className="bg-card border border-border rounded-xl p-5 hover:border-opacity-80 transition-all" data-testid={`discovery-${discovery.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 rounded-lg shrink-0" style={{ backgroundColor: `${discovery.color}20` }}>
          <Icon className="w-5 h-5" style={{ color: discovery.color }} />
        </div>
        <div>
          <h4 className="font-semibold text-sm leading-tight">{discovery.title}</h4>
          <span className="text-xs text-muted-foreground">{discovery.category}</span>
        </div>
      </div>
      <div className="text-3xl font-black mb-1" style={{ color: discovery.color }}>
        {discovery.stat}
      </div>
      <p className="text-xs text-muted-foreground mb-2">{discovery.comparison}</p>
      <p className="text-sm text-muted-foreground leading-relaxed">{discovery.insight}</p>
    </div>
  );
};

const GenderOrientationChart = ({ data }) => {
  if (!data) return null;
  const chartData = ["care", "utility", "creative", "extraction"].map(o => ({
    name: o.charAt(0).toUpperCase() + o.slice(1),
    "Female-Only": data.female_only?.[o] || 0,
    "Male-Only": data.male_only?.[o] || 0,
    "Mixed": data.mixed?.[o] || 0,
  }));
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-primary" />
        Founder Gender x Value Orientation
      </h4>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} barGap={2}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART.gridStroke} />
          <XAxis dataKey="name" tick={CHART.axis} stroke={CHART.axisStroke} interval={0} />
          <YAxis tick={CHART.axis} stroke={CHART.axisStroke} unit="%" />
          <Tooltip {...CHART.tooltip} />
          <Legend wrapperStyle={CHART.legend} />
          <Bar dataKey="Female-Only" fill="#ec4899" radius={CHART.barRadiusV} />
          <Bar dataKey="Male-Only" fill="#3b82f6" radius={CHART.barRadiusV} />
          <Bar dataKey="Mixed" fill="#8b5cf6" radius={CHART.barRadiusV} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const RegionalCareChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const chartData = data.slice(0, 8).map(r => ({
    name: r.country,
    Care: r.care_pct,
    Utility: r.utility_pct,
  }));
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
        <Globe className="w-4 h-4 text-emerald-400" />
        Care vs Utility Ratio by Country
      </h4>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} layout="vertical" barGap={2}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART.gridStroke} />
          <XAxis type="number" tick={CHART.axis} stroke={CHART.axisStroke} unit="%" />
          <YAxis type="category" dataKey="name" tick={CHART.axis} stroke={CHART.axisStroke} width={80} />
          <Tooltip {...CHART.tooltip} />
          <Legend wrapperStyle={CHART.legend} />
          <Bar dataKey="Care" fill="#10b981" radius={CHART.barRadius} />
          <Bar dataKey="Utility" fill="#3b82f6" radius={CHART.barRadius} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const KeyDiscoveries = () => {
  const [data, loading] = usePrefetchOrFetch(
    "discoveryInsights",
    `${API}/discovery-insights`,
    {
      validate: (d) => d && Array.isArray(d.discoveries) && d.discoveries.length > 0,
      delay: 500,
    }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
        <span className="ml-3 text-muted-foreground text-sm">Computing discoveries...</span>
      </div>
    );
  }
  if (!data) return null;

  const coreData = data.discoveries.find(d => d.id === "core_hypothesis")?.data;

  return (
    <div className="space-y-6" data-testid="key-discoveries">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Key Discoveries</h3>
          <p className="text-sm text-muted-foreground">
            From {data.summary.total_apps.toLocaleString()} apps, {data.summary.known_founders.toLocaleString()} with known founders ({data.summary.known_pct}%)
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.discoveries.map(d => (
          <DiscoveryCard key={d.id} discovery={d} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GenderOrientationChart data={coreData} />
        <RegionalCareChart data={data.regional_care_ranking} />
      </div>
    </div>
  );
};

export default KeyDiscoveries;
